import { cn } from "@/shared/lib/utils";
import type { AnchorKey9 } from "../../../api/posterService";

/**
 * 九宫格锚点选择(任务 14 · 7.29 反馈)。
 *
 * ★为什么要它:后端契约**一直是全通的** —— `posterService.ts` 早就声明了
 * `logoAnchor/qrAnchor`,ai 侧 `LayoutOverrides` 收得住(还有更精确的 bbox),
 * 小阳(旁路)也早就能设(`advisor/action_tools.py`)。缺的只是创建页这一个控件,
 * 而 `useFreeCreateActions` 还主动写着 `layoutOverrides: undefined`。
 * 结果是**手动主线比旁路弱**:用户自己做图不能定位置,让 AI 代做反而能。
 *
 * ★「自动」是**一等选项不是空值**:不选 = 让系统按画面留白挑角(`overlay_autoplace`),
 * 那是个真本事,不是"没配置"。所以它在格子里有自己的位置,而不是靠"都不选"表达。
 */
const ROWS: AnchorKey9[][] = [
  ["top_left", "top_center", "top_right"],
  ["middle_left", "middle_center", "middle_right"],
  ["bottom_left", "bottom_center", "bottom_right"],
];

interface Props {
  label: string;
  value: AnchorKey9 | null;
  onChange: (v: AnchorKey9 | null) => void;
  /** 自动挡的说明(海报=按留白挑角;长图=页尾固定位)。 */
  autoHint?: string;
}

export function AnchorGrid9({ label, value, onChange, autoHint }: Props) {
  return (
    <div className="space-y-1.5">
      <div className="flex items-center justify-between gap-2">
        <span className="text-[12.5px] font-medium text-foreground">{label}</span>
        <button
          type="button"
          onClick={() => onChange(null)}
          aria-pressed={value === null}
          className={cn(
            "rounded-md px-2 py-0.5 text-2xs transition-colors cursor-pointer",
            value === null
              ? "bg-primary/10 text-primary font-medium"
              : "text-muted-foreground hover:text-foreground"
          )}
        >
          自动
        </button>
      </div>
      <div
        role="radiogroup"
        aria-label={label}
        className="grid w-[84px] grid-cols-3 gap-[3px] rounded-md border border-border/70 bg-muted/30 p-[3px]"
      >
        {ROWS.flat().map((key) => {
          const active = value === key;
          return (
            <button
              key={key}
              type="button"
              role="radio"
              aria-checked={active}
              aria-label={key}
              onClick={() => onChange(active ? null : key)}
              className={cn(
                "h-[22px] rounded-[3px] transition-colors cursor-pointer",
                active ? "bg-primary" : "bg-background hover:bg-primary/20"
              )}
            />
          );
        })}
      </div>
      {value === null && autoHint && (
        <p className="text-2xs leading-tight text-muted-foreground">{autoHint}</p>
      )}
    </div>
  );
}
